/**
 * ClinicHolidaysCard — rendered inside the Clinic Hours tab.
 *
 * Simple date-flag surface: owner/admin picks a date + optional label
 * (e.g. "Diwali"), we POST it to /api/clinic-holidays. Appointments
 * booked on flagged dates are rejected 409 and the availability
 * calendar renders those days as "Clinic holiday · <label>".
 *
 * No auto-imports, no notifications — purely settings-side flagging.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const todayISO = () => new Date().toISOString().slice(0, 10);
const prettyDate = (iso) => {
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
      weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
};

export default function ClinicHolidaysCard({ canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/clinic-holidays`, {
        params: { upcoming_only: true },
      });
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!date) { setErr('Pick a date first.'); return; }
    setBusy(true);
    setErr(null);
    try {
      await axios.post(`${API}/clinic-holidays`, {
        date,
        label: label.trim() || null,
      });
      setDate('');
      setLabel('');
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not add holiday');
    } finally { setBusy(false); }
  };

  const remove = async (holidayId) => {
    setBusy(true);
    setErr(null);
    try {
      await axios.delete(`${API}/clinic-holidays/${holidayId}`);
      setRows((prev) => prev.filter((h) => h.holiday_id !== holidayId));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not delete');
    } finally { setBusy(false); }
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-lg p-4"
      data-testid="clinic-holidays-card"
    >
      <header className="flex items-center gap-2 mb-1">
        <CalendarOff size={16} className="text-amber-600" />
        <h3 className="text-[13px] font-bold text-slate-900">Holidays</h3>
      </header>
      <p className="text-[11.5px] text-slate-500 mb-3">
        Flag a date as a clinic holiday (e.g. Diwali). Booking is blocked on
        that day for every staff member. Optional label shows up in the
        booking calendar tooltip.
      </p>

      {canEdit && (
        <div
          className="grid grid-cols-1 sm:grid-cols-[160px,1fr,auto] gap-2 mb-3"
          data-testid="clinic-holidays-add-row"
        >
          <input
            type="date"
            value={date}
            min={todayISO()}
            onChange={(e) => setDate(e.target.value)}
            data-testid="clinic-holidays-date-input"
            className="border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label (optional) — e.g. Diwali"
            maxLength={120}
            data-testid="clinic-holidays-label-input"
            className="border border-slate-300 rounded px-2 py-1.5 text-sm"
          />
          <button
            onClick={add}
            disabled={busy || !date}
            data-testid="clinic-holidays-add-btn"
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded"
          >
            <Plus size={13} /> Add holiday
          </button>
        </div>
      )}

      {err && (
        <div
          className="text-[11.5px] px-3 py-1.5 rounded bg-rose-50 border border-rose-200 text-rose-800 mb-3"
          data-testid="clinic-holidays-err"
        >
          {typeof err === 'string' ? err : JSON.stringify(err)}
        </div>
      )}

      {loading ? (
        <div className="text-[12px] text-slate-500 italic">Loading…</div>
      ) : rows.length === 0 ? (
        <div
          className="text-[12px] text-slate-500 italic"
          data-testid="clinic-holidays-empty"
        >
          No upcoming holidays flagged.
        </div>
      ) : (
        <ul
          className="divide-y divide-slate-100 border border-slate-200 rounded"
          data-testid="clinic-holidays-list"
        >
          {rows.map((h) => (
            <li
              key={h.holiday_id}
              className="flex items-center justify-between gap-2 px-3 py-2"
              data-testid={`clinic-holidays-row-${h.holiday_id}`}
            >
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold text-slate-800">
                  {prettyDate(h.date)}
                </div>
                {h.label && (
                  <div className="text-[11px] text-slate-500 truncate">
                    {h.label}
                  </div>
                )}
              </div>
              {canEdit && (
                <button
                  onClick={() => remove(h.holiday_id)}
                  disabled={busy}
                  data-testid={`clinic-holidays-delete-${h.holiday_id}`}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 hover:text-rose-800 disabled:opacity-50 px-2 py-1 rounded hover:bg-rose-50"
                  title="Remove holiday"
                >
                  <Trash2 size={12} /> Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
